import type { UIKitIndentationsProps } from "@qws-org/ui-kit";
import {
  Box,
  Dropdown,
  Flex,
  Image,
  Typography,
  useUiKitComponents,
} from "@qws-org/ui-kit";
import type { FC } from "react";

import type { Action, Pages } from "~/core";
import {
  isExternalLink,
  isInternalLink,
  isTemplateUrl,
} from "~/core/utils/action.utils";
import type { MenuItemType } from "~/features/headerV4/HeaderV4";
import { ArrowDown } from "~/icons";
import { useDirectus } from "~/shared/utils/directus/useDirectus";

type NavbarItemDropdownVariant = "primary" | "secondary";
type NavbarItemProps = MenuItemType & {
  currentNavlink: string;
  onNavlinkClick: (url: string, id: string) => void;
  clickHeaderSub: (targetUrl: string) => void;
  onCallAction: (action: Action) => void;
  getUrl: (page: Pages) => string;
  dropdownVariant?: NavbarItemDropdownVariant;
  offset?: number;
} & Partial<UIKitIndentationsProps> & {
    md?: Partial<UIKitIndentationsProps>;
    d?: Partial<UIKitIndentationsProps>;
  };

type MenuSubItemType = ExtractArrayItemType<NonNullable<MenuItemType>["items"]>;

function isValidDropdownItem(
  item: MenuSubItemType,
): item is NonNullable<MenuSubItemType> {
  return item !== null && item !== undefined;
}

export const NavbarItem: FC<NavbarItemProps> = (props) => {
  const {
    id,
    variant,
    items,
    action,
    translations,
    currentNavlink,
    onNavlinkClick,
    clickHeaderSub,
    getUrl,
    onCallAction,
    dropdownVariant = "primary",
    md,
    d,
    offset = 36,
    ...rest
  } = props;
  const { getDirectusImage } = useDirectus();
  const { Link } = useUiKitComponents();
  const nestedAction = action?.action?.[0];
  let href: string | undefined;

  if (isExternalLink(nestedAction?.item)) {
    if (isTemplateUrl(nestedAction?.item)) {
      href = undefined;
    } else {
      href = nestedAction.item.href ?? undefined;
    }
  }
  if (isInternalLink(nestedAction?.item)) {
    href = nestedAction.item.page ? getUrl(nestedAction.item.page) : undefined;
  }

  const translation = translations?.[0];
  const isActive = currentNavlink === id;

  const onClickDropdownItem = (key: string | number): void => {
    const menuItem = items?.find(
      (item) => item?.menu_action_sub_items_id?.id === key,
    );
    const dropdownAction = menuItem?.menu_action_sub_items_id?.action;
    let dropdownHref: string | undefined;

    if (isExternalLink(dropdownAction?.action?.[0]?.item)) {
      if (isTemplateUrl(dropdownAction?.action?.[0]?.item)) {
        dropdownHref = undefined;
      } else {
        dropdownHref = dropdownAction?.action?.[0]?.item.href ?? undefined;
      }
    }
    if (isInternalLink(dropdownAction?.action?.[0]?.item)) {
      dropdownHref = dropdownAction?.action?.[0]?.item.page
        ? getUrl(dropdownAction?.action?.[0]?.item.page)
        : undefined;
    }
    if (dropdownHref) clickHeaderSub(dropdownHref);

    if (dropdownAction) {
      onCallAction(dropdownAction);
    }
  };

  return (
    <Flex
      {...rest}
      md={{ width: "auto", ...md }}
      align="center"
      display="hidden"
      d={{ display: "flex", ...d }}
      width="100%"
      position={{ type: "relative" }}
    >
      {variant === "default" && (
        <Flex
          align="center"
          justify="space-between"
          hover={{ color: "text-primary-hovered" }}
          color={isActive ? "text-primary-hovered" : "text-primary-default"}
          d={{ m: { mr: 16 } }}
          className="transition-all duration-200 ease-linear"
        >
          {items && (
            <Dropdown.Trigger
              alwaysRenderMenu={true}
              onAction={(key) => {
                onClickDropdownItem(key);
              }}
              strategy="hover"
              offset={offset}
              triggerElement={
                <Link href={href}>
                  <Flex
                    align="center"
                    gap={10}
                    onClick={() => {
                      if (action) {
                        onCallAction(action);
                      }
                    }}
                  >
                    <Typography size="medium">{translation?.name}</Typography>
                    <ArrowDown />
                  </Flex>
                </Link>
              }
              dropdownWrapper={(dropdownWrapperProps) => (
                <Box
                  {...dropdownWrapperProps}
                  bg={
                    dropdownVariant === "secondary"
                      ? "background-secondary"
                      : "background-secondary-transparent"
                  }
                  border={{ radius: "8px" }}
                  backdropBlur={10}
                />
              )}
            >
              {items
                .filter((item) => isValidDropdownItem(item))
                .map((item) => {
                  const controlAction = item?.menu_action_sub_items_id?.action;
                  const itemAction = controlAction?.action?.[0];
                  let itemHref;

                  if (isExternalLink(itemAction?.item)) {
                    if (isTemplateUrl(itemAction?.item)) {
                      itemHref = undefined;
                    } else {
                      itemHref = itemAction.item.href ?? undefined;
                    }
                  }
                  if (isInternalLink(itemAction?.item)) {
                    itemHref = itemAction.item.page
                      ? getUrl(itemAction.item.page)
                      : undefined;
                  }

                  return (
                    <Dropdown.Item
                      key={item.menu_action_sub_items_id?.id}
                      href={itemHref}
                      textValue={
                        item.menu_action_sub_items_id?.translations?.[0]
                          ?.name ?? ""
                      }
                    >
                      <Flex
                        width={322}
                        align="center"
                        gap={8}
                        p={{ px: 16, py: 12 }}
                      >
                        {item?.menu_action_sub_items_id?.icon?.id && (
                          <Image
                            value={getDirectusImage(
                              item.menu_action_sub_items_id.icon.id,
                              {
                                format: "svg",
                                width: 24,
                              },
                            )}
                          />
                        )}
                        <Typography size="medium">
                          {
                            item.menu_action_sub_items_id?.translations?.[0]
                              ?.name
                          }
                        </Typography>
                      </Flex>
                    </Dropdown.Item>
                  );
                })}
            </Dropdown.Trigger>
          )}
        </Flex>
      )}

      {variant === "thin" && (
        <Flex
          align="center"
          justify="space-between"
          className="transition-all duration-200  ease-linear "
          d={{ m: { mr: 16 } }}
        >
          {items && (
            <Dropdown.Trigger
              alwaysRenderMenu={true}
              onAction={(key) => onClickDropdownItem(key)}
              strategy="hover"
              offset={offset}
              triggerElement={
                <Link href={href}>
                  <Flex
                    align="center"
                    gap={10}
                    onClick={() => {
                      if (action) {
                        onCallAction(action);
                      }
                    }}
                  >
                    <Typography
                      color="text-secondary-default"
                      hover={{ color: "text-primary-default" }}
                      size="medium"
                    >
                      {translation?.name}
                    </Typography>
                    <ArrowDown />
                  </Flex>
                </Link>
              }
              dropdownWrapper={(dropdownWrapperProps) => (
                <Box
                  {...dropdownWrapperProps}
                  bg={
                    dropdownVariant === "secondary"
                      ? "background-secondary"
                      : "background-secondary-transparent"
                  }
                  border={{ radius: "8px" }}
                  backdropBlur={10}
                />
              )}
            >
              {items
                .filter((item) => isValidDropdownItem(item))
                .map((item) => {
                  const itemAction =
                    item.menu_action_sub_items_id?.action?.action?.[0];
                  let itemHref;
                  if (isExternalLink(itemAction?.item)) {
                    if (isTemplateUrl(itemAction?.item)) {
                      href = undefined;
                    } else {
                      href = itemAction.item.href ?? undefined;
                    }
                  }
                  if (isInternalLink(itemAction?.item)) {
                    itemHref = itemAction.item.page
                      ? getUrl(itemAction.item.page)
                      : undefined;
                  }
                  return (
                    <Dropdown.Item
                      key={item.menu_action_sub_items_id?.id}
                      href={itemHref}
                      textValue={
                        item.menu_action_sub_items_id?.translations?.[0]
                          ?.name ?? ""
                      }
                    >
                      <Flex
                        width={322}
                        align="center"
                        gap={8}
                        p={{ px: 16, py: 12 }}
                        color="text-primary-default"
                      >
                        {item?.menu_action_sub_items_id?.icon?.id && (
                          <Image
                            value={getDirectusImage(
                              item.menu_action_sub_items_id.icon.id,
                              {
                                format: "svg",
                                width: 24,
                              },
                            )}
                          />
                        )}
                        <Typography size="medium">
                          {
                            item.menu_action_sub_items_id?.translations?.[0]
                              ?.name
                          }
                        </Typography>
                      </Flex>
                    </Dropdown.Item>
                  );
                })}
            </Dropdown.Trigger>
          )}
        </Flex>
      )}

      {variant === "underline" && (
        <Flex
          align="center"
          justify="center"
          bg="surface-accent2-default"
          border={{ radius: "4px" }}
          p={{ px: 16, py: 4 }}
        >
          <Link
            href={href}
            onClick={() => {
              if (href) onNavlinkClick(href, id);
              if (action) {
                onCallAction(action);
              }
            }}
          >
            <Typography
              size="medium"
              weight="semibold"
              color="text-primary-inverted"
            >
              {translation?.name}
            </Typography>
          </Link>
        </Flex>
      )}

      {variant === "emphasized" && (
        <Flex
          align="center"
          groupParent
          className="transition-all duration-200 ease-linear"
        >
          <Box
            bg="/icons/vip-star-icon.svg"
            hover={{
              bgGroup: "/icons/vip-star-icon-hover.svg",
            }}
            minWidth={24}
            minHeight={24}
            m={{ mx: 4 }}
          />
          <Link
            href={href}
            onClick={() => {
              if (href) onNavlinkClick(href, id);
              if (action) {
                onCallAction(action);
              }
            }}
          >
            <Typography
              size="small"
              weight="semibold"
              color="text-accent-default"
              hover={{ color: "text-accent-hovered" }}
            >
              {translation?.name}
            </Typography>
          </Link>
          <Box
            bg="/icons/vip-star-icon.svg"
            hover={{
              bgGroup: "/icons/vip-star-icon-hover.svg",
            }}
            m={{ mx: 4 }}
            minWidth={24}
            minHeight={24}
          />
        </Flex>
      )}

      {variant === "highlighted" && (
        <Box
          p={{ px: 16, py: 4 }}
          border={{ radius: "4px" }}
          bg="surface-accent1-default"
        >
          <Link
            href={href}
            onClick={() => {
              if (href) onNavlinkClick(href, id);
              if (action) {
                onCallAction(action);
              }
            }}
          >
            <Typography
              size="medium"
              weight="semibold"
              horizontalAlign="center"
              color="text-primary-default"
            >
              {translation?.name}
            </Typography>
          </Link>
        </Box>
      )}
    </Flex>
  );
};
